import { Calendar, Clock, MapPin, Plus, User } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DemoNavigator } from "../../components/demo/DemoNavigator";
import { PageBanner } from "../../components/ui/PageBanner";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import { useI18nStore } from "../../stores/i18nStore";
import type { Translation } from "../../types/i18n";
import { i18nToast } from "../../utils/i18nToast";

type AppointmentType = "checkup" | "travel" | "vaccination" | "followUp";

// The stored type is a code; its label comes from i18n at render.
const APPOINTMENT_TYPE_KEYS: Record<AppointmentType, keyof Translation> = {
  checkup: "apptTypeCheckup",
  travel: "apptTypeTravel",
  vaccination: "apptTypeVaccination",
  followUp: "apptTypeFollowUp",
};

interface Appointment {
  id: string;
  date: string;
  time: string;
  provider: string;
  type: AppointmentType;
  location: string;
  status: "upcoming" | "completed" | "cancelled";
  notes?: string;
}

const AppointmentPage: React.FC = () => {
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [showBookingForm, setShowBookingForm] = useState(false);

  useEffect(() => {
    loadAppointments();
  }, []);

  const loadAppointments = async () => {
    try {
      setLoading(true);
      // TODO: Load from backend API
      // Mock data for now
      setAppointments([
        {
          id: "1",
          date: "2026-02-05",
          time: "10:00",
          provider: "Dr. Schmidt",
          type: "checkup",
          location: "Health Center Berlin",
          status: "upcoming",
          notes: "Annual health checkup",
        },
        {
          id: "2",
          date: "2026-01-15",
          time: "14:30",
          provider: "Dr. Müller",
          type: "travel",
          location: "Travel Clinic Munich",
          status: "completed",
        },
      ]);
    } catch (error: any) {
      console.error("Failed to load appointments:", error);
      i18nToast.error(useI18nStore.getState().t.apptLoadFailed);
    } finally {
      setLoading(false);
    }
  };

  const upcomingAppointments = appointments.filter(
    (a) => a.status === "upcoming",
  );
  const pastAppointments = appointments.filter((a) => a.status === "completed");

  const typeLabel = (type: AppointmentType) =>
    String(t[APPOINTMENT_TYPE_KEYS[type]] ?? type);
  // Date and time are stored separately (ISO date, 24-hour "HH:mm").
  const when = (a: Appointment) => fmt.dateTime(`${a.date}T${a.time}`);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <PageBanner
          icon={Calendar}
          title={t.apptTitle}
          description={t.apptDescription}
        />
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">{t.loading}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Page Banner */}
      <PageBanner
        icon={Calendar}
        title={t.apptTitle}
        description={t.apptDescription}
      />

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200">
        <nav className="-mb-px flex space-x-5 overflow-x-auto">
          <button
            onClick={() => navigate("/traveler/health")}
            className="border-b-2 border-transparent py-3 px-1 text-sm font-medium text-gray-500 hover:text-gray-700 whitespace-nowrap"
          >
            {t.edit}
          </button>
          <button
            onClick={() => navigate("/traveler/health/emergency")}
            className="border-b-2 border-transparent py-3 px-1 text-sm font-medium text-gray-500 hover:text-gray-700 whitespace-nowrap"
          >
            {t.apptTabEmergency}
          </button>
          <button className="border-b-2 border-blue-500 py-3 px-1 text-sm font-medium text-blue-600 whitespace-nowrap">
            {t.apptTabAppointments}
          </button>
          <button
            onClick={() => navigate("/traveler/health/checkin")}
            className="border-b-2 border-transparent py-3 px-1 text-sm font-medium text-gray-500 hover:text-gray-700 whitespace-nowrap"
          >
            {t.apptTabCheckin}
          </button>
          <button
            onClick={() => navigate("/traveler/health/insurance-request")}
            className="border-b-2 border-transparent py-3 px-1 text-sm font-medium text-gray-500 hover:text-gray-700 whitespace-nowrap"
          >
            {t.apptTabInsurance}
          </button>
        </nav>
      </div>

      {/* Quick Actions */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowBookingForm(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          <span>{t.apptBook}</span>
        </button>
      </div>

      {/* Upcoming Appointments */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <Calendar className="w-5 h-5 mr-2 text-blue-600" />
          {t.apptUpcomingHeading.replace(
            "{count}",
            String(upcomingAppointments.length),
          )}
        </h2>

        {upcomingAppointments.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">{t.apptNoUpcoming}</p>
            <button
              onClick={() => setShowBookingForm(true)}
              className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
            >
              {t.apptBookFirst}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {upcomingAppointments.map((appointment) => (
              <div
                key={appointment.id}
                className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {t.apptStatusUpcoming}
                      </span>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {typeLabel(appointment.type)}
                      </h3>
                    </div>

                    <div className="space-y-2 mt-3">
                      <div className="flex items-center text-sm text-gray-600">
                        <User className="w-4 h-4 mr-2" />
                        {appointment.provider}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Clock className="w-4 h-4 mr-2" />
                        {when(appointment)}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPin className="w-4 h-4 mr-2" />
                        {appointment.location}
                      </div>
                      {appointment.notes && (
                        <p className="text-sm text-gray-600 mt-2 italic">
                          {t.apptNote.replace("{note}", appointment.notes)}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col space-y-2 ml-4">
                    <button className="px-3 py-1 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50">
                      {t.apptReschedule}
                    </button>
                    <button className="px-3 py-1 text-sm border border-red-300 text-red-700 rounded hover:bg-red-50">
                      {t.cancel}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Past Appointments */}
      {pastAppointments.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <Clock className="w-5 h-5 mr-2 text-gray-600" />
            {t.apptPastHeading.replace(
              "{count}",
              String(pastAppointments.length),
            )}
          </h2>

          <div className="space-y-4">
            {pastAppointments.map((appointment) => (
              <div
                key={appointment.id}
                className="border border-gray-200 rounded-lg p-4 bg-gray-50"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
                        {t.apptStatusCompleted}
                      </span>
                      <h3 className="text-lg font-semibold text-gray-700">
                        {typeLabel(appointment.type)}
                      </h3>
                    </div>

                    <div className="space-y-2 mt-3">
                      <div className="flex items-center text-sm text-gray-600">
                        <User className="w-4 h-4 mr-2" />
                        {appointment.provider}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Clock className="w-4 h-4 mr-2" />
                        {when(appointment)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Booking Form Dialog */}
      {showBookingForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {t.apptBook}
              </h3>
              <button
                onClick={() => setShowBookingForm(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label={t.close}
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label
                  htmlFor="appointment-type"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  {t.apptType}
                </label>
                <select
                  id="appointment-type"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  {(
                    Object.keys(APPOINTMENT_TYPE_KEYS) as AppointmentType[]
                  ).map((type) => (
                    <option key={type} value={type}>
                      {typeLabel(type)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="appointment-date"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  {t.apptDate}
                </label>
                <input
                  id="appointment-date"
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  min={new Date().toISOString().split("T")[0]}
                />
              </div>

              <div>
                <label
                  htmlFor="appointment-time"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  {t.apptTime}
                </label>
                <input
                  id="appointment-time"
                  type="time"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label
                  htmlFor="appointment-notes"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  {t.apptNotes}
                </label>
                <textarea
                  id="appointment-notes"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows={3}
                  placeholder={t.apptNotesPlaceholder}
                />
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowBookingForm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                {t.cancel}
              </button>
              <button
                onClick={() => {
                  i18nToast.success(t.apptComingSoon);
                  setShowBookingForm(false);
                }}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {t.apptBook}
              </button>
            </div>
          </div>
        </div>
      )}

      <DemoNavigator currentStep={5} />
    </div>
  );
};

export default AppointmentPage;
